import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
} from '@mui/material';

interface RenameThreadDialogProps {
  open: boolean;
  onClose: () => void;
  onRename: (newTitle: string) => Promise<void>;
  currentTitle: string;
  threadId: string;
}

const RenameThreadDialog: React.FC<RenameThreadDialogProps> = ({
  open,
  onClose,
  onRename,
  currentTitle,
  threadId,
}) => {
  const [newTitle, setNewTitle] = useState(currentTitle);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setNewTitle(currentTitle);
      setError(null);
    }
  }, [open, currentTitle]);

  const handleSave = async () => {
    if (!newTitle.trim()) {
      setError('Thread title cannot be empty');
      return;
    }

    if (newTitle.trim() === currentTitle) {
      onClose();
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onRename(newTitle.trim());
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to rename thread');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !loading) {
      handleSave();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Rename Chat Thread
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Thread Title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyPress={handleKeyPress}
            error={!!error}
            helperText={error || 'Enter a new name for this chat thread'}
            disabled={loading}
            variant="outlined"
            sx={{ mt: 1 }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          disabled={loading || !newTitle.trim()}
          variant="contained"
          color="primary"
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameThreadDialog;
